import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const DocumentsContext = createContext({});

export function DocumentsProvider({ children }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem("crescendo:docsActiveId") || null; } catch { return null; }
  });
  // Open tabs (multi-doc tab bar in DocumentsView). Persist across sessions.
  const [tabs, setTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crescendo:docsTabs") || "[]"); } catch { return []; }
  });

  const openTab = useCallback((doc_id) => {
    if (!doc_id) return;
    setTabs((t) => {
      const next = t.includes(doc_id) ? t : [...t, doc_id];
      try { localStorage.setItem("crescendo:docsTabs", JSON.stringify(next)); } catch {}
      return next;
    });
    setActiveId(doc_id);
    try { localStorage.setItem("crescendo:docsActiveId", doc_id); } catch {}
  }, []);
  const closeTab = useCallback((doc_id) => {
    setTabs((t) => t.filter((x) => x !== doc_id));
    setActiveId((cur) => {
      if (cur !== doc_id) return cur;
      // Pick the previous tab if any, else null.
      const idx = tabs.indexOf(doc_id);
      const next = tabs[idx - 1] || tabs[idx + 1] || null;
      return next;
    });
  }, [tabs]);

  const reload = useCallback(async () => {
    if (!user) { setDocs([]); return; }
    try {
      const { data } = await api.get("/documents");
      setDocs(data || []);
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const create = async (body) => {
    const { data } = await api.post("/documents", body);
    setDocs((d) => [data, ...d]);
    return data;
  };
  const update = async (doc_id, body) => {
    const { data } = await api.put(`/documents/${doc_id}`, body);
    // Preserve local properties unless the server response explicitly has them
    // (prevents autosave from overwriting patchDoc'd master-doc properties)
    setDocs((d) => d.map((x) => {
      if (x.doc_id !== doc_id) return x;
      // If body included properties, server response is authoritative
      if (body && body.properties !== undefined) return data;
      // Otherwise preserve whatever properties we have locally
      return { ...data, properties: x.properties ?? data.properties };
    }));
    return data;
  };
  // patchDoc — local-only merge, no API call (used by master doc props, etc.)
  const patchDoc = useCallback((doc_id, patch) => {
    setDocs(d => d.map(x => x.doc_id === doc_id ? { ...x, ...patch } : x));
  }, []);

  const remove = async (doc_id) => {
    await api.delete(`/documents/${doc_id}`);
    setDocs((d) => d.filter((x) => x.doc_id !== doc_id));
  };

  return (
    <DocumentsContext.Provider value={{ docs, create, update, remove, patchDoc, reload, activeId, setActiveId, tabs, openTab, closeTab, setTabs }}>
      {children}
    </DocumentsContext.Provider>
  );
}

export const useDocuments = () => useContext(DocumentsContext);
